import { DateInput } from "@msuite/ui/date-input";
import { Divider } from "@msuite/ui/divider";
import { FormCheckbox, Select } from "@msuite/ui/form";
import { useStream } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { StreamTaskList } from "@msuite/ui/misc";
import { Modal } from "@msuite/ui/modal";
import { moment } from "@msuite/utils/date";
import type { ProjectTimeline } from "@msuite/utils/projects";
import {
	type GetProjectTimelineDiaryQuerySchema,
	getProjectTimelineDiaryQuerySchema,
} from "@msuite/utils/schemas";
import { FormProvider, useForm } from "react-hook-form";
import { FileCardRemote } from "@/components/files";

/** Props Interface */
interface CreateDiaryModalProps {
	isOpen: boolean;
	onClose: () => void;
	projectId: string;
	timeline: ProjectTimeline;
}

export function CreateDiaryModal({ isOpen, onClose, timeline, projectId }: CreateDiaryModalProps) {
	const startDate = timeline.at(-1)?.date ?? moment().db();
	const endDate = timeline.at(0)?.date ?? moment().db();

	/** Hooks */
	const { t } = useTranslation();
	const methods = useForm({
		resolver: resolver(getProjectTimelineDiaryQuerySchema),
		defaultValues: {
			startDate,
			endDate,
			order: "asc" as const,
			includeNotes: true,
			includeMedia: true,
			includeUnpublishedAssignments: true,
		},
	});

	const stream = useStream(
		`${process.env.PUBLIC_SERVER_URL}/projects/${projectId}/timeline/diary`,
		{
			startStreamOnMount: false,
			resetDataOnInput: true,
		},
	);

	async function handleOnSubmit(json: GetProjectTimelineDiaryQuerySchema) {
		await stream.startStream({
			body: json,
		});
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				isOpen={isOpen}
				onClose={onClose}
				title={t("projects.create_diary")}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
			>
				<Modal.Body>
					<div className="v-stack gap-6">
						<div className="grid grid-cols-2 gap-x-4">
							<DateInput
								control={methods.control}
								name="startDate"
								label={t("dates.start_date")}
								isRequired
								minDate={moment(startDate)}
								maxDate={moment(endDate)}
							/>
							<DateInput
								control={methods.control}
								name="endDate"
								label={t("dates.end_date")}
								isRequired
								minDate={moment(startDate)}
								maxDate={moment(endDate)}
							/>
						</div>
						<Select
							control={methods.control}
							name="order"
							label={t("general.order_direction")}
							options={[
								{
									value: "asc",
									label: t("projects.create_diary_asc"),
									item: {},
								},
								{
									value: "desc",
									label: t("projects.create_diary_desc"),
									item: {},
								},
							]}
							renderOption={(_, label, value) => (
								<div className="h-stack gap-2">
									{value === "asc" ? (
										<Icon name="CalendarArrowDown" />
									) : (
										<Icon name="CalendarArrowUp" />
									)}
									<p>{label}</p>
								</div>
							)}
							isNullable={false}
							isRequired
							itemNotFoundLabel={t("general.not_found")}
						/>
						<div className="v-stack">
							<FormCheckbox
								control={methods.control}
								name="includeNotes"
							>
								{t("projects.include_notes_in_diary")}
							</FormCheckbox>
							<FormCheckbox
								control={methods.control}
								name="includeMedia"
							>
								{t("projects.include_media_in_diary")}
							</FormCheckbox>
							<FormCheckbox
								control={methods.control}
								name="includeUnpublishedAssignments"
							>
								{t("projects.include_unpublished_assignments_in_diary")}
							</FormCheckbox>
						</div>
						<Divider variant="modal" />
						<StreamTaskList
							stream={stream}
							render={(text) => {
								try {
									const json = JSON.parse(text) as {
										type: "result";
										result: "success";
										fileId: string;
									};
									if (!json.fileId) throw new Error("No fileId found in response");
									return (
										<div className="v-stack mt-2 gap-5">
											<Divider variant="modal" />
											<FileCardRemote
												fileId={json.fileId}
												onInteraction={async () => {}}
												withRemove={false}
											/>
										</div>
									);
								} catch {
									return null;
								}
							}}
						/>
					</div>
				</Modal.Body>
				<Modal.FooterForm />
			</Modal>
		</FormProvider>
	);
}
